import { GrowthBook, configureCache } from "@growthbook/growthbook";
import type { Experiment, Result } from "@growthbook/growthbook";
import { isProductionEnv } from "@/utils/IdentifyGlobalEnv";

/**
 * Normalized experiment exposure, produced ONLY when a GrowthBook Experiment
 * rule actually evaluated (payload loaded, inside coverage, not intercepted
 * by a force rule). Force-listed tenants never produce one — they are outside
 * the experiment sample by design.
 */
export type ExperimentAssignment = {
  testId: string;
  testVariant: string;
  variationId: number;
  featuresLoaded: true;
};

/**
 * Builds a GrowthBook trackingCallback that maps SDK experiment results to a
 * normalized assignment. The variant name comes from the experiment payload
 * (variation meta) — never derived from the feature value, which is what made
 * default-false evaluations masquerade as the control arm.
 */
export function createExperimentTrackingCallback(
  onAssignment: (assignment: ExperimentAssignment) => void,
): (experiment: Experiment<unknown>, result: Result<unknown>) => void {
  return (experiment, result) => {
    onAssignment({
      testId: experiment.key,
      testVariant: result.name || result.key,
      variationId: result.variationId,
      featuresLoaded: true,
    });
  };
}

/**
 * GrowthBook service singleton that wraps the @growthbook/growthbook SDK.
 * Provides feature flag and A/B testing capabilities with Amplitude integration.
 */
export class GrowthBookService {
  private static instance: GrowthBookService;
  private gb: GrowthBook | null = null;
  private initPromise: Promise<unknown> | null = null;
  private isInitialized = false;
  private currentAttributes: Record<string, unknown> = {};

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private constructor() {}

  static getInstance(): GrowthBookService {
    if (!GrowthBookService.instance) {
      GrowthBookService.instance = new GrowthBookService();
    }
    return GrowthBookService.instance;
  }

  /**
   * Initialize GrowthBook with user attributes
   * @param attributes - User attributes for targeting
   */
  async init(
    attributes: Record<string, unknown> = {},
    onExperimentAssignment?: (assignment: ExperimentAssignment) => void,
  ): Promise<void> {
    const apiHost = process.env.VUE_APP_GROWTHBOOK_API_HOST || "https://api.growthbook.docplanner.io";
    const clientKey = process.env.VUE_APP_GROWTHBOOK_CLIENT_KEY || "sdk-dHutv0X0J4fxuXJ";

    if (!clientKey || clientKey === "" || clientKey === "undefined" || clientKey === undefined) {
      console.warn("[GrowthBook] No client key provided, skipping initialization");
      return;
    }

    if (!isProductionEnv()) {
      console.log("[GrowthBook] Cache disabled for testing");
    }

    this.currentAttributes = { ...attributes };
    console.log("currentAttributes", this.currentAttributes);

    this.gb = new GrowthBook({
      apiHost,
      clientKey,
      attributes: this.currentAttributes,
      ...(onExperimentAssignment
        ? { trackingCallback: createExperimentTrackingCallback(onExperimentAssignment) }
        : {}),
    });
    console.log("gb", this.gb);

    this.initPromise = this.gb.init({ streaming: true });

    try {
      await this.initPromise;
      this.isInitialized = true;

      if (!isProductionEnv()) {
        console.log("[GrowthBook] Initialized successfully", {
          apiHost,
          attributes: this.currentAttributes,
          featuresCount: Object.keys(this.gb.getFeatures()).length,
        });
      }
    } catch (error) {
      if (!isProductionEnv()) {
        console.error("[GrowthBook] Initialization failed", error);
      }
      throw error;
    }
  }

  /**
   * Set or merge user attributes
   * @param attrs - Attributes to merge with existing ones
   */
  setAttributes(attrs: Record<string, unknown>): void {
    if (!this.gb) {
      if (!isProductionEnv()) {
        console.warn("[GrowthBook] Cannot set attributes, not initialized");
      }
      return;
    }

    this.currentAttributes = { ...this.currentAttributes, ...attrs };
    this.gb.setAttributes(this.currentAttributes);

    if (!isProductionEnv()) {
      console.log("[GrowthBook] Attributes updated", this.currentAttributes);
    }
  }

  /**
   * Check if a feature is enabled
   * @param key - Feature key
   * @returns true if feature is on, false otherwise
   */
  isOn(key: string): boolean {
    if (!this.gb) {
      return false;
    }
    return this.gb.isOn(key);
  }

  /**
   * Get feature value with fallback
   * @param key - Feature key
   * @param fallback - Fallback value if feature is not found
   * @returns Feature value or fallback
   */
  getFeatureValue<T = unknown>(key: string, fallback: T): T {
    if (!this.gb) {
      return fallback;
    }
    return this.gb.getFeatureValue(key, fallback) as T;
  }

  /**
   * Check if GrowthBook is ready (initialized and loaded features)
   * @returns true if ready, false otherwise
   */
  isReady(): boolean {
    return this.isInitialized && this.gb !== null;
  }

  /**
   * Whether the features payload was actually loaded.
   * isReady() can be true with an EMPTY payload (init resolved but the fetch
   * failed) — in that state every flag evaluates to its default and experiment
   * assignments must not be trusted.
   * @returns true only if at least one feature definition is present
   */
  hasLoadedFeatures(): boolean {
    if (!this.gb) {
      return false;
    }
    return Object.keys(this.gb.getFeatures()).length > 0;
  }

  /**
   * Wait for GrowthBook to be ready with timeout
   * @param timeoutMs - Timeout in milliseconds (default: 3000)
   * @returns Promise that resolves to true if ready, false if timeout
   */
  async waitForReady(timeoutMs = 3000): Promise<boolean> {
    if (this.isReady()) {
      return true;
    }

    if (!this.initPromise) {
      return false;
    }

    return Promise.race([
      this.initPromise.then(() => true),
      new Promise<boolean>((resolve) => {
        setTimeout(() => resolve(false), timeoutMs);
      }),
    ]).catch(() => false);
  }

  /**
   * Get all features (useful for DevPanel)
   * @returns All features from GrowthBook
   */
  getAllFeatures(): Record<string, unknown> {
    if (!this.gb) {
      return {};
    }
    return this.gb.getFeatures();
  }

  /**
   * Force specific feature values (useful for testing)
   * @param map - Map of feature keys to values
   */
  setForcedFeatures(map: Record<string, unknown>): void {
    if (!this.gb) {
      if (!isProductionEnv()) {
        console.warn("[GrowthBook] Cannot set forced features, not initialized");
      }
      return;
    }

    // GrowthBook expects a Map, convert Record to Map
    const featureMap = new Map(Object.entries(map));
    this.gb.setForcedFeatures(featureMap);

    if (!isProductionEnv()) {
      console.log("[GrowthBook] Forced features set", map);
    }
  }

  /**
   * Get current user attributes
   * @returns Current attributes
   */
  getAttributes(): Record<string, unknown> {
    return { ...this.currentAttributes };
  }

  /**
   * Destroy the GrowthBook instance and reset singleton
   */
  destroy(): void {
    if (this.gb) {
      this.gb.destroy();
      this.gb = null;
    }

    this.isInitialized = false;
    this.initPromise = null;
    this.currentAttributes = {};

    if (!isProductionEnv()) {
      console.log("[GrowthBook] Instance destroyed");
    }
  }
}
